import { FunctionComponent } from 'react';
import { Badge, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { Stage, StageType } from '@/model';

const PREFIX = 'pages.application_championship_stage_standings.context_banner';

type StandingsContextBannerProps = {
  championshipName?: string;
  seasonYear?: number;
  stage?: Stage;
  teamCount?: number;
};

const BADGE_COLOR: Record<StageType, string> = {
  [StageType.LEAGUE]: 'green',
  [StageType.GROUP]: 'purple',
  [StageType.KNOCKOUT]: 'orange',
};

const BADGE_KEY: Record<StageType, string> = {
  [StageType.LEAGUE]: 'league',
  [StageType.GROUP]: 'group',
  [StageType.KNOCKOUT]: 'knockout',
};

export const StandingsContextBanner: FunctionComponent<StandingsContextBannerProps> = ({
  championshipName,
  seasonYear,
  stage,
  teamCount,
}) => {
  const { t } = useTranslation();
  if (!stage || !championshipName || !seasonYear) return null;

  const isKnockout = stage.type === StageType.KNOCKOUT;
  const stageLabel = stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name;

  const tone = isKnockout
    ? { bg: 'orange.50', border: 'orange.100', color: 'orange.700' }
    : { bg: 'purple.50', border: 'purple.100', color: 'brand.700' };

  return (
    <Flex
      align="center"
      gap={3}
      bg={tone.bg}
      border="1px solid"
      borderColor={tone.border}
      borderRadius="md"
      px={4}
      py={3}
    >
      <ChakraFeatherIcon name="info" boxSize="16px" color={tone.color} />
      <Text fontSize="sm" color={tone.color} flex="1">
        {championshipName} · {seasonYear} · {stageLabel}
        {isKnockout
          ? ` · ${t(`${PREFIX}.knockout_suffix`)}`
          : ` · ${t(`${PREFIX}.team_count`, { count: teamCount ?? 0 })}`}
      </Text>
      <Badge colorScheme={BADGE_COLOR[stage.type]} borderRadius="full" px={2} py="2px">
        {t(`${PREFIX}.badge.${BADGE_KEY[stage.type]}`)}
      </Badge>
    </Flex>
  );
};
